import React, { useEffect } from 'react';

import { CloseIcon } from '../../icons/CloseIcon';
import { Body, CloseButton, Footer, Overlay, Panel } from './Drawer.styles';
import type { DrawerProps } from './Drawer.types';

let scrollLockCount = 0;
let savedBodyOverflow = '';
let savedBodyPaddingRight = '';

function acquireBodyScrollLock() {
  if (scrollLockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    savedBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }
  scrollLockCount += 1;
}

function releaseBodyScrollLock() {
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedBodyOverflow;
    document.body.style.paddingRight = savedBodyPaddingRight;
    savedBodyOverflow = '';
    savedBodyPaddingRight = '';
  }
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  'aria-label': ariaLabel,
  closeLabel,
  closeIcon,
  footer,
  width,
  mobileTopOffset,
  backdropBlur = false,
  placement = 'right',
  desktopOffset,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    acquireBodyScrollLock();
    return () => releaseBodyScrollLock();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const panelStyle = {
    ...(width ? { '--drawer-width': width } : {}),
    ...(desktopOffset ? { '--drawer-desktop-offset': desktopOffset } : {}),
    ...(mobileTopOffset ? { '--drawer-mobile-top': mobileTopOffset } : {}),
  } as React.CSSProperties;

  const overlayStyle = (
    desktopOffset ? { '--drawer-desktop-offset': desktopOffset } : {}
  ) as React.CSSProperties;

  return (
    <>
      <Overlay
        data-open={String(open)}
        data-blur={String(backdropBlur)}
        data-placement={placement}
        onClick={onClose}
        aria-hidden="true"
        style={overlayStyle}
      />
      <Panel
        data-open={String(open)}
        data-placement={placement}
        role="dialog"
        aria-modal={open}
        aria-label={ariaLabel}
        aria-hidden={!open}
        style={panelStyle}
      >
        <CloseButton type="button" onClick={onClose} aria-label={closeLabel}>
          {closeIcon ?? <CloseIcon size={20} />}
        </CloseButton>
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </>
  );
};
